/**
 * Shared, app-agnostic display-label formatters for Amplitude analytics.
 *
 * These live in `@shared/helpers` (rather than being duplicated per MFE) so every
 * NAOM surface — order grid, summary, landing page — emits identical, analyst-ready
 * values and respects the no app-to-app import rule.
 */
/** Default theme display label used when no `data-theme` is set / not in a browser. */
export const DEFAULT_THEME_LABEL = 'Ford Light';
/** Dealer brand single-letter codes → display names. */
const BRAND_LABELS = {
    F: 'Ford',
    L: 'Lincoln',
};
/**
 * Map a brand code to its display label.
 * Already-expanded values ("Ford" / "Lincoln") and unknown values pass through
 * unchanged so we never blank out data we don't recognize.
 */
export function formatBrand(brand) {
    var _a;
    if (!brand)
        return '';
    return (_a = BRAND_LABELS[brand]) !== null && _a !== void 0 ? _a : brand;
}
/** `data-theme` attribute values → display labels. */
const THEME_LABELS = {
    ford_light: 'Ford Light',
    ford_dark: 'Ford Dark',
    lincoln_light: 'Lincoln Light',
    lincoln_dark: 'Lincoln Dark',
};
/** Map a raw theme token (e.g. `ford_light`) to its display label. */
export function formatTheme(theme) {
    var _a;
    if (!theme)
        return DEFAULT_THEME_LABEL;
    return (_a = THEME_LABELS[theme]) !== null && _a !== void 0 ? _a : theme;
}
/** Read the active theme from the DOM and return its display label. */
export function getCurrentThemeLabel() {
    if (typeof document === 'undefined')
        return DEFAULT_THEME_LABEL;
    return formatTheme(document.documentElement.getAttribute('data-theme'));
}
/**
 * Strip the query string and hash from a URL before sending it to analytics.
 * Query/hash segments can embed identifiers (dealer codes, tokens, etc.) that
 * must not leak into Amplitude — consistent with `page viewed` events, which
 * only forward `window.location.pathname`. Returns `origin + pathname`; falls
 * back to the value sans `?`/`#` if the URL cannot be parsed.
 */
export function sanitizeLinkUrl(href) {
    if (!href)
        return '';
    try {
        const base = typeof window !== 'undefined' ? window.location.origin : undefined;
        const url = new URL(href, base);
        // Only http(s) links are safe to forward. Other schemes (mailto:, tel:)
        // put identifiers (email/phone) in the path, so drop them entirely.
        if (url.protocol !== 'http:' && url.protocol !== 'https:')
            return '';
        return `${url.origin}${url.pathname}`;
    }
    catch (_a) {
        return href.split(/[?#]/)[0];
    }
}
